import type { LocationDescriptorObject } from "history";
import type { TFunction } from "i18next";
import { generatePath } from "react-router-dom";
import routing from "routing";
import type {
	QuickFilter,
	SearchResponse,
	SearchSnippet,
	SearchType,
} from "src/api/search";
import { defaultDetails } from "src/components/NewComponents/Search/ExamsSearch";
import { removePolishSign } from "src/helpers/formatHtmlTag";

export type SearchItem = {
	key: string;
	to: LocationDescriptorObject;
	title: string;
	meta: string;
	snippet?: SearchSnippet;
};

export type Section = {
	type: SearchType;
	title: string;
	count: number;
	showAllLabel: string;
	showAllTo: LocationDescriptorObject;
	items: SearchItem[];
};

const searchPath = (type: SearchType) =>
	generatePath(routing.paths.searchQuestion, {
		type: routing.searchTypes[type],
	});

// /szukaj/pytan z frazą i opcjonalnie zaznaczonym szybkim filtrem
export const questionsSearchTo = (
	searchValue: string,
	quickFilter?: QuickFilter,
): LocationDescriptorObject => ({
	pathname: searchPath("questions"),
	state: {
		searchValue,
		additionalOption: {
			...defaultDetails.additionalOption,
			...(quickFilter ? { [quickFilter]: true } : {}),
		},
	},
});

// /szukaj/fiszek z frazą i opcjonalnie kategorią (author na stronie decków)
export const decksSearchTo = (
	searchValue: string,
	category?: string,
): LocationDescriptorObject => ({
	pathname: searchPath("decks"),
	state: { searchValue, ...(category ? { author: category } : {}) },
});

const htmlToText = (html: string) =>
	new DOMParser().parseFromString(html, "text/html").body.textContent ?? "";

// "answer_d" → "w odpowiedzi D", reszta 1:1 z kluczem
export const snippetLabel = (source: SearchSnippet["source"], t: TFunction) =>
	source.startsWith("answer_")
		? t("globalSearch.snippetSource.answer", {
				letter: source.slice(-1).toUpperCase(),
			})
		: t(`globalSearch.snippetSource.${source}`);

const withSnippet = (snippets: SearchSnippet[] | undefined) =>
	snippets?.[0] ? { snippet: snippets[0] } : {};

type BuildParams = {
	data: SearchResponse;
	searchValue: string;
	t: TFunction;
};

export function buildSections({
	data,
	searchValue,
	t,
}: BuildParams): Section[] {
	const sections: Section[] = [];

	if (data.questions) {
		const to = questionsSearchTo(searchValue);
		sections.push({
			type: "questions",
			title: t("globalSearch.sections.questions", "Pytania"),
			count: data.questions.count,
			showAllLabel: t("globalSearch.showAll.questions", "Wszystkie pytania"),
			showAllTo: to,
			items: data.questions.items.map((question) => ({
				key: `question-${question.id}`,
				// strona wyników zawężona do pytania (state.questions → ids); id nie trafia do pola,
				// w polu chip z treścią (questionLabel), ✕ zdejmuje filtr
				to: {
					...to,
					state: {
						...(to.state as object),
						questions: [question.id],
						questionLabel: htmlToText(question.pytanie),
					},
				},
				title: htmlToText(question.pytanie),
				meta: [question.dzial, question.termin].filter(Boolean).join(" · "),
				...withSnippet(question.snippets),
			})),
		});
	}

	if (data.decks) {
		sections.push({
			type: "decks",
			title: t("globalSearch.sections.decks", "Zestawy fiszek"),
			count: data.decks.totalCount,
			showAllLabel: t("globalSearch.showAll.decks", "Wszystkie zestawy"),
			showAllTo: decksSearchTo(searchValue),
			items: data.decks.items.map((deck) => ({
				key: `deck-${deck.id}`,
				to: {
					pathname: searchPath("decks"),
					state: { searchValue: deck.name },
				},
				title: deck.name,
				meta: [
					t("decks.search.flashcardsCount", { count: deck.flashcardsCount }),
					deck.isOwn && t("globalSearch.ownDeck", "Twój zestaw"),
					deck.isCompanyDeck &&
						t("globalSearch.companyDeck", "Oficjalny zestaw"),
				]
					.filter(Boolean)
					.join(" · "),
				...withSnippet(deck.snippets),
			})),
		});
	}

	if (data.articles) {
		sections.push({
			type: "articles",
			title: t("globalSearch.sections.articles", "Artykuły"),
			count: data.articles.count,
			showAllLabel: t("globalSearch.showAll.articles", "Wszystkie artykuły"),
			showAllTo: { pathname: searchPath("articles"), state: { searchValue } },
			items: data.articles.items.map((article) => ({
				key: `article-${article.id}`,
				to: {
					pathname: generatePath(routing.paths.article, {
						id: article.id,
						topic: removePolishSign(article.title),
					}),
				},
				title: article.title,
				meta: article.departments.join(", "),
				...withSnippet(article.snippets),
			})),
		});
	}

	return sections;
}
